import { useEffect, useMemo, useState } from "react";
import { useContent } from "@/context/ContentContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, AlertCircle, MapPin, Palette, CreditCard, MessageCircle } from "lucide-react";

const experiences = [
  { name: "Private Tufting Parties", desc: "Book the studio just for your group." },
  { name: "Sip", desc: "BYOB creative night with friends." },
  { name: "Tuft Buddy Parties", desc: "Perfect for kids and family celebrations." },
  { name: "Doormat Painting", desc: "Fun, quick, and everyone leaves with a keepsake." },
  { name: "Patch Hat Workshops", desc: "Design your own iron-on patch hat." },
  { name: "Other Creative Experiences", desc: "Custom experiences — just ask." },
];

const audiences = ["Birthdays", "Girls' Nights", "Family Events", "Bachelorettes", "Date Nights", "Youth Groups", "Church Groups", "Celebrations"];

export default function PrivateEvents() {
  const pc = useContent().pages.private_events;
  const [cfg, setCfg] = useState(null);
  const [serviceCfg, setServiceCfg] = useState([]);
  const [themes, setThemes] = useState([]);
  const [mobileCfg, setMobileCfg] = useState(null);
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    event_type: "Rug Tufting",
    preferred_date: "", alternate_date: "", preferred_time: "",
    guests: 6, age_range: "", experience: "",
    location: "studio", budget: "", notes: "",
    rug_size_id: "", addon_ids: [], extra_time_blocks: 0,
    tuft_sip_acknowledged: false,
    event_terms_accepted: false,
    theme_pref: "", theme_notes: "",
    address: "", city: "", state: "", zip_code: "",
    venue_name: "", parking_info: "", access_info: "", setup_restrictions: "", location_notes: "",
  });
  const [result, setResult] = useState(null); // { id, breakdown }
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/booking-config").then((r) => {
      setCfg(r.data);
      const firstSize = r.data.rug_sizes?.[0]?.id || "";
      const minG = r.data.rules?.min_guests || 6;
      setForm((f) => ({ ...f, rug_size_id: firstSize, guests: Math.max(f.guests, minG) }));
    });
    api.get("/service-config").then((r) => setServiceCfg(r.data || []));
    api.get("/workshops?is_theme=true").then((r) => setThemes(r.data || [])).catch(() => {});
    api.get("/settings/public").then((r) => setMobileCfg(r.data?.mobile_config || null)).catch(() => {});
  }, []);

  const currentService = useMemo(() => serviceCfg.find((s) => s.label === form.event_type) || serviceCfg[0], [serviceCfg, form.event_type]);
  const showRugSizes = currentService?.show_rug_sizes !== false;
  const sipEnabled = currentService?.sip_enabled !== false;
  const sipSelected = form.addon_ids.includes("tuft_sip");
  const isRugTufting = (form.event_type || "").toLowerCase().includes("rug tufting");
  const isMobile = form.location === "mobile";

  // Theme options for Rug Tufting — reuse existing themes from Admin, dedup by name
  const themeOptions = useMemo(() => {
    const names = Array.from(new Set((themes || []).map((t) => t.name).filter(Boolean)));
    return [...names, "Other / Not Sure Yet"];
  }, [themes]);

  useEffect(() => {
    if (!currentService) return;
    setForm((f) => ({
      ...f,
      rug_size_id: showRugSizes ? (f.rug_size_id || cfg?.rug_sizes?.[0]?.id || "") : "",
      addon_ids: sipEnabled ? f.addon_ids : f.addon_ids.filter((a) => a !== "tuft_sip"),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentService?.label]);

  const breakdown = useMemo(() => computeBreakdown(cfg, form, currentService), [cfg, form, currentService]);
  const minGuests = cfg?.rules?.min_guests ?? 6;

  const toggleAddon = (id) => setForm((f) => ({ ...f, addon_ids: f.addon_ids.includes(id) ? f.addon_ids.filter((x) => x !== id) : [...f.addon_ids, id] }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.guests < minGuests) return toast.error(`Minimum ${minGuests} guests required`);
    if (sipSelected && !form.tuft_sip_acknowledged) return toast.error("Please acknowledge the Sip terms");
    if (!form.event_terms_accepted) return toast.error("Please accept the deposit terms to submit");
    setBusy(true);
    try {
      const { data } = await api.post("/private-events", { ...form, guests: Number(form.guests), extra_time_blocks: Number(form.extra_time_blocks) });
      setResult(data);
      toast.success("Request sent — check your inbox.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send request");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="max-w-3xl">
          <div className="text-terracotta text-sm tracking-widest uppercase font-medium">{pc.eyebrow}</div>
          <h1 className="mt-2 font-serif font-bold text-5xl md:text-6xl leading-tight">{pc.heading}</h1>
          <p className="mt-4 text-lg text-charcoal/70">{pc.description}</p>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {experiences.map((e, i) => (
            <div key={i} className="bg-white rounded-3xl p-8 shadow-card">
              <div className="w-10 h-10 rounded-full bg-ochre grid place-items-center"><Sparkles size={18} className="text-charcoal"/></div>
              <h3 className="mt-4 font-serif text-xl font-semibold">{e.name}</h3>
              <p className="mt-2 text-sm text-charcoal/70">{e.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-cream-alt py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="font-serif text-3xl md:text-4xl font-semibold text-center">Perfect for</h2>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            {audiences.map((a) => (<div key={a} className="px-5 py-2.5 rounded-full bg-white text-charcoal shadow-card text-sm font-medium">{a}</div>))}
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 lg:px-8 py-24" id="request">
        <div className="text-center mb-10">
          <div className="text-terracotta text-sm tracking-widest uppercase font-medium">Get started</div>
          <h2 className="mt-2 font-serif font-bold text-4xl md:text-5xl">Request a Private Event</h2>
          <p className="mt-3 text-charcoal/70 max-w-2xl mx-auto">Quote requests do not reserve the date. Your date is only secured after we approve your request AND receive your deposit.</p>
        </div>

        {result ? (
          <div className="bg-white rounded-3xl p-10 md:p-14 shadow-card text-center max-w-3xl mx-auto" data-testid="private-event-confirm">
            <h3 className="font-serif text-3xl font-semibold text-forest">Request received!</h3>
            <p className="mt-3 text-charcoal/70">Check your inbox — we've sent a confirmation with your itemized estimate.</p>
            <div className="mt-6 rounded-2xl bg-ochre/10 border border-ochre/40 p-5 text-left" data-testid="pe-confirm-deposit-notice">
              <p className="text-sm text-charcoal/80">
                A <b>50% deposit</b> is required to secure your event date. The <b className="text-terracotta uppercase tracking-wide">remaining balance is due 7 days before the event</b>.
              </p>
              <p className="mt-2 text-xs text-charcoal-muted">Your date is not confirmed until we review availability and receive your deposit. We'll email you a secure deposit link once your request is approved.</p>
            </div>
            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              <button type="button" disabled data-testid="pe-pay-deposit"
                className="rounded-full bg-terracotta text-white px-6 py-3 font-semibold inline-flex items-center justify-center gap-2 shadow-button opacity-50 cursor-not-allowed"
                title="Available after your request is approved">
                <CreditCard size={16}/> Pay Deposit
              </button>
              <a href="mailto:info@thatstuft.com?subject=More info about my private event request" data-testid="pe-more-info"
                className="rounded-full bg-charcoal text-cream px-6 py-3 font-semibold inline-flex items-center justify-center gap-2">
                <MessageCircle size={16}/> Request More Information
              </a>
            </div>
            <p className="mt-3 text-xs text-charcoal-muted">Pay Deposit unlocks once we've confirmed availability for your date.</p>
          </div>
        ) : !cfg ? (
          <div className="text-center text-charcoal-muted">Loading pricing…</div>
        ) : (
          <div className="grid lg:grid-cols-5 gap-8">
            <form onSubmit={submit} className="lg:col-span-3 bg-white rounded-3xl p-8 shadow-card space-y-6" data-testid="private-event-form">
              <Section title="About you">
                <div className="grid md:grid-cols-2 gap-4">
                  <F label="Name *" v={form.name} on={(v) => setForm({...form, name: v})} req name="name"/>
                  <F label="Email *" v={form.email} on={(v) => setForm({...form, email: v})} type="email" req name="email"/>
                  <F label="Phone" v={form.phone} on={(v) => setForm({...form, phone: v})} name="phone"/>
                  <Sel label="Event Type" v={form.event_type} on={(v) => setForm({...form, event_type: v, addon_ids: [], theme_pref: "", theme_notes: ""})} opts={serviceCfg.length ? serviceCfg.map((s) => s.label) : experiences.map((e) => e.name)} name="event_type"/>
                </div>
              </Section>

              <Section title="When & where">
                <div className="grid md:grid-cols-2 gap-4">
                  <F label="Preferred Date" v={form.preferred_date} on={(v) => setForm({...form, preferred_date: v})} type="date" name="preferred_date"/>
                  <F label="Alternate Date" v={form.alternate_date} on={(v) => setForm({...form, alternate_date: v})} type="date" name="alternate_date"/>
                  <F label="Preferred Time" v={form.preferred_time} on={(v) => setForm({...form, preferred_time: v})} type="time" name="preferred_time"/>
                  <Sel label="Studio or Mobile" v={form.location} on={(v) => setForm({...form, location: v})} opts={[["studio","Studio (at That's Tuft)"],["mobile","Mobile / We Come to You"]]} name="location"/>
                </div>

                {isMobile && (
                  <div className="mt-6 rounded-2xl border-2 border-ochre/30 bg-ochre/5 p-5 space-y-4" data-testid="pe-mobile-block">
                    <div className="flex items-start gap-2">
                      <MapPin size={18} className="text-terracotta mt-0.5 shrink-0"/>
                      <div>
                        <div className="font-serif text-lg">Mobile event details</div>
                        <div className="text-xs text-charcoal-muted">Tell us where we're headed so we can quote travel and setup accurately.</div>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      <F label="Venue / Business Name" v={form.venue_name} on={(v) => setForm({...form, venue_name: v})} name="venue_name"/>
                      <F label="Street Address" v={form.address} on={(v) => setForm({...form, address: v})} name="address"/>
                      <F label="City" v={form.city} on={(v) => setForm({...form, city: v})} name="city"/>
                      <F label="State" v={form.state} on={(v) => setForm({...form, state: v})} name="state"/>
                      <F label="ZIP Code" v={form.zip_code} on={(v) => setForm({...form, zip_code: v})} name="zip_code"/>
                    </div>
                    <TA label="Parking / Loading Info" v={form.parking_info} on={(v) => setForm({...form, parking_info: v})} name="parking_info" placeholder="Where can we park and unload gear?"/>
                    <TA label="Stairs / Elevator / Access Instructions" v={form.access_info} on={(v) => setForm({...form, access_info: v})} name="access_info" placeholder="Steps, elevator, freight, security desk, etc."/>
                    <TA label="Setup Restrictions" v={form.setup_restrictions} on={(v) => setForm({...form, setup_restrictions: v})} name="setup_restrictions" placeholder="Space limits, floor protection, noise, timing…"/>
                    <TA label="Additional Location Notes" v={form.location_notes} on={(v) => setForm({...form, location_notes: v})} name="location_notes"/>
                    <div className="text-xs text-charcoal/75 bg-white rounded-xl p-3 border border-cream-alt" data-testid="pe-mobile-disclaimer">
                      {mobileCfg?.disclaimer || "Mobile events may include additional travel, transportation, setup, and/or staffing fees based on event location, distance, guest count, and event requirements. Any additional fees will be included in your final quote before your event is confirmed."}
                    </div>
                    {mobileCfg?.out_of_area_notice && (
                      <div className="text-xs text-terracotta bg-terracotta/5 border border-terracotta/30 rounded-xl p-3" data-testid="pe-outside-area-notice">
                        {mobileCfg.out_of_area_notice}
                      </div>
                    )}
                  </div>
                )}
              </Section>

              {isRugTufting && (
                <Section title="Design direction">
                  <div className="rounded-2xl bg-cream-alt/50 p-5" data-testid="pe-theme-block">
                    <div className="flex items-start gap-2">
                      <Palette size={18} className="text-terracotta mt-0.5 shrink-0"/>
                      <div>
                        <div className="font-serif text-lg">What theme or design style are you looking for?</div>
                        <div className="text-xs text-charcoal-muted">Pick from our current themes, choose "Other / Not Sure Yet", and add any notes below.</div>
                      </div>
                    </div>
                    <div className="mt-4 grid sm:grid-cols-2 md:grid-cols-3 gap-2" data-testid="pe-theme-options">
                      {themeOptions.map((name) => {
                        const selected = form.theme_pref === name;
                        return (
                          <button key={name} type="button" onClick={() => setForm({...form, theme_pref: name})}
                            data-testid={`pe-theme-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            className={`text-left rounded-2xl p-3 border-2 transition-colors ${selected ? "border-terracotta bg-terracotta/5" : "border-cream-alt hover:border-terracotta/40 bg-white"}`}>
                            <div className="text-sm font-semibold">{name}</div>
                          </button>
                        );
                      })}
                    </div>
                    <div className="mt-4">
                      <label className="text-sm font-medium">Design notes</label>
                      <textarea data-testid="pe-theme-notes" rows={3} value={form.theme_notes} onChange={(e) => setForm({...form, theme_notes: e.target.value})}
                        placeholder="Colors, vibe, specific ideas, references…"
                        className="mt-1 w-full rounded-2xl border border-cream-alt px-4 py-3 focus:ring-2 focus:ring-terracotta focus:outline-none"/>
                    </div>
                  </div>
                </Section>
              )}

              <Section title="Choose your experience">
                {showRugSizes && (
                  <>
                    <label className="text-sm font-medium">Rug Size (per person)</label>
                    <div className="mt-2 grid sm:grid-cols-3 gap-3">
                      {cfg.rug_sizes.map((s) => (
                        <button key={s.id} type="button" onClick={() => setForm({...form, rug_size_id: s.id})} data-testid={`pe-size-${s.id}`}
                          className={`text-left rounded-2xl p-4 border-2 transition-colors ${form.rug_size_id === s.id ? "border-terracotta bg-terracotta/5" : "border-cream-alt hover:border-terracotta/40"}`}>
                          <div className="font-serif text-lg font-semibold">{s.label}</div>
                          <div className="text-xs text-charcoal-muted">{s.dimensions}</div>
                          <div className="mt-2 font-serif text-terracotta text-lg">${s.price_per_person}<span className="text-xs text-charcoal-muted">/guest</span></div>
                        </button>
                      ))}
                    </div>
                  </>
                )}

                {currentService?.options?.length > 0 && (
                  <div className={showRugSizes ? "mt-6" : ""}>
                    <label className="text-sm font-medium">{currentService.label} Options</label>
                    <div className="mt-2 grid sm:grid-cols-2 gap-3">
                      {currentService.options.map((o) => (
                        <div key={o.id} className="rounded-2xl p-4 border-2 border-cream-alt bg-white">
                          <div className="font-serif text-lg">{o.label}</div>
                          <div className="mt-1 font-serif text-terracotta">${o.price_per_person}<span className="text-xs text-charcoal-muted"> /guest</span></div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {sipEnabled && cfg.addons.length > 0 && (
                  <div className="mt-6">
                    <label className="text-sm font-medium">Add-ons</label>
                    <div className="mt-2 grid gap-2">
                      {cfg.addons.map((a) => (
                        <label key={a.id} className={`flex items-center justify-between rounded-2xl p-4 border-2 cursor-pointer ${form.addon_ids.includes(a.id) ? "border-terracotta bg-terracotta/5" : "border-cream-alt"}`}>
                          <div className="flex items-center gap-3">
                            <input data-testid={`pe-addon-${a.id}`} type="checkbox" checked={form.addon_ids.includes(a.id)} onChange={() => toggleAddon(a.id)} className="w-4 h-4 accent-terracotta"/>
                            <div>
                              <div className="font-medium">{a.id === "tuft_sip" ? "Sip" : a.label}</div>
                              {a.description && <div className="text-xs text-charcoal-muted">{a.description}</div>}
                            </div>
                          </div>
                          <div className="font-serif text-terracotta">+${a.price_per_person}<span className="text-xs text-charcoal-muted">/guest</span></div>
                        </label>
                      ))}
                    </div>
                    {sipSelected && cfg.tuft_sip_disclaimer && (
                      <div className="mt-4 rounded-2xl bg-ochre/10 border-2 border-ochre p-4" data-testid="pe-sip-disclaimer">
                        <div className="text-xs text-charcoal-muted whitespace-pre-line">{cfg.tuft_sip_disclaimer}</div>
                        <label className="mt-3 flex items-start gap-2 cursor-pointer text-sm">
                          <input data-testid="pe-sip-ack" type="checkbox" checked={form.tuft_sip_acknowledged} onChange={(e) => setForm({...form, tuft_sip_acknowledged: e.target.checked})} className="mt-1 accent-terracotta"/>
                          <span className="font-semibold">I have read and agree to the Sip terms.</span>
                        </label>
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-6 grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Guests (minimum {minGuests})</label>
                    <input data-testid="pe-guests" type="number" min={minGuests} value={form.guests} onChange={(e) => setForm({...form, guests: e.target.value})} className={`mt-1 w-full rounded-2xl border px-4 py-3 focus:ring-2 focus:ring-terracotta focus:outline-none ${form.guests < minGuests ? "border-red-400" : "border-cream-alt"}`}/>
                    {form.guests < minGuests && <div className="mt-1 text-xs text-red-600 flex items-center gap-1"><AlertCircle size={12}/> Minimum {minGuests} guests</div>}
                  </div>
                  <div>
                    <label className="text-sm font-medium">Extra Time (${cfg.rules?.extra_time_fee ?? 50} / {cfg.rules?.extra_time_block_minutes ?? 30} min)</label>
                    <select data-testid="pe-extra-time" value={form.extra_time_blocks} onChange={(e) => setForm({...form, extra_time_blocks: e.target.value})} className="mt-1 w-full rounded-2xl border border-cream-alt px-4 py-3 bg-white focus:ring-2 focus:ring-terracotta focus:outline-none">
                      {[0,1,2,3,4].map((n) => <option key={n} value={n}>{n === 0 ? "None" : `+${n * (cfg.rules?.extra_time_block_minutes ?? 30)} min`}</option>)}
                    </select>
                  </div>
                </div>
              </Section>

              <Section title="Details">
                <div className="grid md:grid-cols-2 gap-4">
                  <F label="Age Range" v={form.age_range} on={(v) => setForm({...form, age_range: v})} placeholder="e.g. 21+, all ages" name="age_range"/>
                  <F label="Budget (optional)" v={form.budget} on={(v) => setForm({...form, budget: v})} name="budget"/>
                </div>
                <div className="mt-4">
                  <label className="text-sm font-medium">Additional Notes</label>
                  <textarea data-testid="pe-notes" rows={3} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="mt-1 w-full rounded-2xl border border-cream-alt px-4 py-3 focus:ring-2 focus:ring-terracotta focus:outline-none"/>
                </div>
              </Section>

              <div className="rounded-2xl bg-ochre/10 border-2 border-ochre p-5" data-testid="pe-deposit-notice">
                  A <b>50% deposit</b> is required to secure your event date. The <b className="text-terracotta uppercase tracking-wide">remaining balance is due 7 days before the event</b>.
                <label className="mt-3 flex items-start gap-2 text-sm cursor-pointer">
                  <input data-testid="pe-terms-ack" type="checkbox" checked={form.event_terms_accepted} onChange={(e) => setForm({...form, event_terms_accepted: e.target.checked})} className="mt-1 accent-terracotta"/>
                  <span>I understand and accept the deposit &amp; balance terms.</span>
                </label>
              </div>

              <div className="pt-2">
                <button data-testid="pe-submit" type="submit" disabled={busy || !form.event_terms_accepted || form.guests < minGuests} className="w-full rounded-full bg-terracotta text-white px-8 py-4 font-semibold shadow-button disabled:opacity-50">
                  {busy ? "Sending…" : "Send Request"}
                </button>
                <p className="mt-3 text-xs text-charcoal-muted text-center">This sends a request. Your date is only reserved after our approval and your deposit payment.</p>
              </div>
            </form>

            <aside className="lg:col-span-2 lg:sticky lg:top-24 h-fit">
              <div className="bg-charcoal text-cream rounded-3xl p-8 shadow-hover" data-testid="pe-breakdown">
                <div className="text-ochre text-xs tracking-widest uppercase font-medium">Your itemized estimate</div>
                <h3 className="mt-2 font-serif text-2xl">Preview</h3>
                <div className="mt-6 space-y-3 text-sm">
                  <Row label={breakdown.base_label ? `${breakdown.base_label} × ${breakdown.guests}` : (currentService ? `Choose your ${currentService.label} options` : "Choose an event type")} value={`$${breakdown.rug_subtotal.toFixed(2)}`}/>
                  {breakdown.addons.map((a) => (
                    <Row key={a.id} label={`${a.label} × ${breakdown.guests}`} value={`$${(a.price_per_person * breakdown.guests).toFixed(2)}`}/>
                  ))}
                  {breakdown.extra_time_subtotal > 0 && (
                    <Row label={`Extra time (${breakdown.extra_time_blocks} × ${breakdown.extra_time_block_minutes}min)`} value={`$${breakdown.extra_time_subtotal.toFixed(2)}`}/>
                  )}
                </div>
                <div className="mt-6 pt-6 border-t border-white/15 space-y-2">
                  <Row label="Subtotal" value={`$${breakdown.total.toFixed(2)}`} bold/>
                  <Row label={`Deposit (${Math.round(breakdown.deposit_pct)}%)`} value={`$${breakdown.deposit_amount.toFixed(2)}`}/>
                  <Row label={`Balance (due ${breakdown.balance_due_days} days before)`} value={`$${breakdown.balance_amount.toFixed(2)}`}/>
                </div>
                <div className="mt-6 text-xs text-cream/70">
                  Not a final quote. Your date is not reserved until we approve your request and receive the deposit.
                </div>
              </div>
            </aside>
          </div>
        )}
      </section>
    </div>
  );
}

function computeBreakdown(cfg, form, service) {
  const empty = { rug_size: null, addons: [], guests: 0, rug_subtotal: 0, addons_subtotal: 0, extra_time_subtotal: 0, total: 0, deposit_pct: 50, deposit_amount: 0, balance_amount: 0, extra_time_blocks: 0, extra_time_block_minutes: 30, balance_due_days: 7, service_label: "", base_label: "" };
  if (!cfg) return empty;
  const guests = Number(form.guests) || 0;
  const showRugSizes = service ? service.show_rug_sizes !== false : true;
  const size = showRugSizes ? (cfg.rug_sizes.find((s) => s.id === form.rug_size_id) || null) : null;
  const addons = cfg.addons.filter((a) => form.addon_ids.includes(a.id));
  const rules = cfg.rules || {};
  let rug_ppg = 0;
  let base_label = "";
  if (showRugSizes && size) {
    rug_ppg = size.price_per_person;
    base_label = size.label;
  } else if (!showRugSizes && service) {
    rug_ppg = (service.options || []).reduce((s, o) => s + (Number(o.price_per_person) || 0), 0);
    base_label = service.label;
  }
  const rug_subtotal = rug_ppg * guests;
  const addons_subtotal = addons.reduce((s, a) => s + a.price_per_person, 0) * guests;
  const et = Number(form.extra_time_blocks) || 0;
  const et_fee = rules.extra_time_fee ?? 50;
  const extra_time_subtotal = et * et_fee;
  const total = rug_subtotal + addons_subtotal + extra_time_subtotal;
  const dp = rules.deposit_pct ?? 50;
  const dep = +(total * dp / 100).toFixed(2);
  const bal = +(total - dep).toFixed(2);
  return {
    rug_size: size, addons, guests, base_label,
    service_label: service?.label || "",
    rug_subtotal: +rug_subtotal.toFixed(2), addons_subtotal: +addons_subtotal.toFixed(2),
    extra_time_subtotal, total: +total.toFixed(2),
    deposit_pct: dp, deposit_amount: dep, balance_amount: bal,
    extra_time_blocks: et, extra_time_block_minutes: rules.extra_time_block_minutes ?? 30,
    balance_due_days: rules.balance_due_days ?? 7,
  };
}

function Section({ title, children }) {
  return (
    <div>
      <div className="font-serif text-xl font-semibold mb-3">{title}</div>
      {children}
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className={`flex items-center justify-between ${bold ? "font-serif text-lg" : ""}`}>
      <span className="text-cream/80">{label}</span>
      <span className={bold ? "text-ochre" : ""}>{value}</span>
    </div>
  );
}

function F({ label, v, on, type = "text", req, name, ...rest }) {
  return (
    <div>
      <label className="text-sm font-medium">{label}</label>
      <input data-testid={`pe-${name}`} required={req} type={type} value={v} onChange={(e) => on(e.target.value)} {...rest}
        className="mt-1 w-full rounded-2xl border border-cream-alt px-4 py-3 focus:ring-2 focus:ring-terracotta focus:outline-none"/>
    </div>
  );
}

function TA({ label, v, on, name, placeholder }) {
  return (
    <div>
      <label className="text-sm font-medium">{label}</label>
      <textarea data-testid={`pe-${name}`} rows={2} value={v} onChange={(e) => on(e.target.value)} placeholder={placeholder}
        className="mt-1 w-full rounded-2xl border border-cream-alt px-4 py-3 focus:ring-2 focus:ring-terracotta focus:outline-none"/>
    </div>
  );
}

function Sel({ label, v, on, opts, name }) {
  const options = opts.map((o) => Array.isArray(o) ? o : [o, o]);
  return (
    <div>
      <label className="text-sm font-medium">{label}</label>
      <select data-testid={`pe-${name}`} value={v} onChange={(e) => on(e.target.value)} className="mt-1 w-full rounded-2xl border border-cream-alt px-4 py-3 bg-white focus:ring-2 focus:ring-terracotta focus:outline-none">
        {options.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
      </select>
    </div>
  );
}
